// API client — typed wrapper around fetch.
// All requests go to /api/* which Vite proxies to the Worker in dev,
// and Cloudflare routes directly in production.
// Auth tokens come from Clerk via setTokenGetter(); no localStorage required.

const BASE = "/api";

// Set by App.tsx from Clerk's useAuth().getToken — called before each request
let clerkGetToken: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: () => Promise<string | null>): void {
  clerkGetToken = fn;
}

async function resolveToken(): Promise<string | null> {
  if (clerkGetToken) {
    try {
      return await clerkGetToken();
    } catch {
      return null;
    }
  }
  return null;
}

// Fetches an R2 asset with auth headers and returns a blob: URL safe for <img src>.
const assetBlobCache = new Map<string, Promise<string>>();

export function getAssetBlobUrl(r2Key: string): Promise<string> {
  if (assetBlobCache.has(r2Key)) return assetBlobCache.get(r2Key)!;

  const promise = (async () => {
    const token = await resolveToken();
    const res = await fetch(`${BASE}/assets/${r2Key}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Asset fetch failed: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  })();

  assetBlobCache.set(r2Key, promise);
  return promise;
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await resolveToken();

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
      error?: string;
    };
    throw new ApiError(err.error ?? res.statusText, res.status);
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const api = {
  auth: {
    me: () =>
      request<{ userId: string; email: string; profile: UserProfile | null }>(
        "GET",
        "/auth/me",
      ),
    logout: () => request<{ success: boolean }>("POST", "/auth/logout"),
  },

  // ─── User profile ──────────────────────────────────────────────────────────
  me: {
    patchProfile: (data: Partial<UserProfile>) =>
      request<{ profile: UserProfile }>("PATCH", "/me/profile", data),
  },

  // ─── Social connections ────────────────────────────────────────────────────
  // All social methods are brand-scoped. Pass the active brandId so connections
  // are stored and retrieved per-brand rather than globally per-user.
  social: {
    list: (brandId: string) =>
      request<{ connections: SocialConnection[] }>(
        "GET",
        `/social/connections?brandId=${encodeURIComponent(brandId)}`,
      ),
    disconnect: (provider: SocialProvider, brandId: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/social/connections/${provider}?brandId=${encodeURIComponent(brandId)}`,
      ),
    /**
     * Starts an OAuth flow for the given provider scoped to brandId.
     * returnPath is the frontend path the backend should redirect to after
     * OAuth completes — defaults to /setup/social if omitted.
     */
    startOAuth: (
      provider: SocialProvider,
      brandId: string,
      returnPath?: string,
    ) => {
      const params = new URLSearchParams({ brandId });
      if (returnPath) params.set("returnPath", returnPath);
      return request<{ authUrl: string }>(
        "GET",
        `/social/${provider}/start?${params}`,
      );
    },
    /**
     * Connects a Bluesky account using the AT Protocol App Password flow.
     * No OAuth redirect — credentials are exchanged directly for JWTs.
     */
    connectBluesky: (handle: string, appPassword: string, brandId: string) =>
      request<{ success: true; displayName: string; did: string }>(
        "POST",
        "/social/bluesky/connect",
        { handle, appPassword, brandId },
      ),
  },

  // ─── Brands ───────────────────────────────────────────────────────────────
  brands: {
    list: () => request<{ brands: Brand[] }>("GET", "/brands"),
    get: (brandId: string) =>
      request<{ brand: Brand }>("GET", `/brands/${brandId}`),
    create: (data: Partial<Brand>) =>
      request<{ brand: Brand }>("POST", "/brands", data),
    update: (brandId: string, data: Partial<Brand>) =>
      request<{ brand: Brand }>("PUT", `/brands/${brandId}`, data),
    delete: (brandId: string) =>
      request<{ success: true }>("DELETE", `/brands/${brandId}`),
    analyzeUrl: (url: string) =>
      request<{ suggestion: BrandSuggestion; blocked?: boolean }>(
        "POST",
        "/brands/analyze-url",
        { url },
      ),
    analyzeText: (pastedText: string) =>
      request<{ suggestion: BrandSuggestion }>("POST", "/brands/analyze-url", {
        pastedText,
      }),
    suggestVoice: (context: {
      name?: string;
      industry?: string;
      targetAudience?: string;
    }) =>
      request<{ suggestion: VoiceSuggestion }>(
        "POST",
        "/brands/suggest-voice",
        context,
      ),
  },

  // ─── Campaigns ────────────────────────────────────────────────────────────
  campaigns: {
    list: (brandId: string) =>
      request<{ campaigns: Campaign[] }>("GET", `/brands/${brandId}/campaigns`),
    get: (brandId: string, campaignId: string) =>
      request<{ campaign: Campaign }>(
        "GET",
        `/brands/${brandId}/campaigns/${campaignId}`,
      ),
    create: (brandId: string, data: Partial<Campaign>) =>
      request<{ campaign: Campaign }>(
        "POST",
        `/brands/${brandId}/campaigns`,
        data,
      ),
    update: (brandId: string, campaignId: string, data: Partial<Campaign>) =>
      request<{ campaign: Campaign }>(
        "PUT",
        `/brands/${brandId}/campaigns/${campaignId}`,
        data,
      ),
    delete: (brandId: string, campaignId: string) =>
      request<{ success: true }>(
        "DELETE",
        `/brands/${brandId}/campaigns/${campaignId}`,
      ),
    analyzeUrl: (brandId: string, url: string) =>
      request<{
        suggestion: CampaignSuggestion;
        suggestedImageUrls?: string[];
        blocked?: boolean;
      }>("POST", `/brands/${brandId}/campaigns/analyze-url`, { url }),
    analyzeText: (brandId: string, pastedText: string) =>
      request<{ suggestion: CampaignSuggestion }>(
        "POST",
        `/brands/${brandId}/campaigns/analyze-url`,
        { pastedText },
      ),
    importImageFromUrl: (
      brandId: string,
      campaignId: string,
      imageUrl: string,
    ) =>
      request<{ r2Key: string }>(
        "POST",
        `/brands/${brandId}/campaigns/${campaignId}/assets/from-url`,
        { imageUrl },
      ),
    uploadImage: async (
      brandId: string,
      campaignId: string,
      file: File,
    ): Promise<{ r2Key: string }> => {
      const token = await resolveToken();
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch(
        `${BASE}/brands/${brandId}/campaigns/${campaignId}/assets/upload`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        },
      );
      if (!res.ok) {
        const err = (await res
          .json()
          .catch(() => ({ error: res.statusText }))) as { error?: string };
        throw new ApiError(err.error ?? res.statusText, res.status);
      }
      return res.json() as Promise<{ r2Key: string }>;
    },
    deleteAsset: (brandId: string, campaignId: string, filename: string) =>
      request<{ success: true }>(
        "DELETE",
        `/brands/${brandId}/campaigns/${campaignId}/assets/${filename}`,
      ),
  },

  // ─── Posts ────────────────────────────────────────────────────────────────
  posts: {
    list: (params?: {
      status?: string;
      brandId?: string;
      campaignId?: string;
      platform?: string;
      limit?: number;
      offset?: number;
    }) => {
      const qs = new URLSearchParams(
        Object.entries(params ?? {})
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      );
      return request<{ posts: Post[]; total: number }>("GET", `/posts?${qs}`);
    },
    get: (postId: string) => request<{ post: Post }>("GET", `/posts/${postId}`),
    update: (postId: string, data: Partial<Post>) =>
      request<{ post: Post }>("PUT", `/posts/${postId}`, data),
    approve: (postId: string) =>
      request<{ post: Post }>("POST", `/posts/${postId}/approve`, {}),
    reject: (postId: string, note?: string) =>
      request<{ post: Post }>("POST", `/posts/${postId}/reject`, { note }),
    approveGroup: (groupId: string) =>
      request<{ groupId: string; approved: number }>(
        "POST",
        `/posts/groups/${groupId}/approve`,
        {},
      ),
    rejectGroup: (groupId: string, note?: string) =>
      request<{ groupId: string; rejected: number }>(
        "POST",
        `/posts/groups/${groupId}/reject`,
        { note },
      ),
    scheduleGroup: (groupId: string, scheduledAt: number) =>
      request<{ groupId: string; scheduled: number }>(
        "POST",
        `/posts/groups/${groupId}/schedule`,
        { scheduledAt },
      ),
    schedule: (postId: string, scheduledAt: number) =>
      request<{ post: Post }>("POST", `/posts/${postId}/schedule`, {
        scheduledAt,
      }),
    markPublished: (postId: string) =>
      request<{ post: Post }>("POST", `/posts/${postId}/mark-published`, {}),
    publish: (postId: string) =>
      request<{ post: Post; platformPostId: string; url?: string }>(
        "POST",
        `/posts/${postId}/publish`,
        {},
      ),
    publishGroup: (groupId: string) =>
      request<{
        groupId: string;
        published: number;
        results: { postId: string; platformPostId: string }[];
      }>("POST", `/posts/groups/${groupId}/publish`, {}),
    engagement: (postId: string) =>
      request<{ engagement: PostEngagement }>(
        "GET",
        `/posts/${postId}/engagement`,
      ),
    revertToPending: (postId: string) =>
      request<{ post: Post }>("POST", `/posts/${postId}/pending`, {}),
    sendForRevision: (postId: string, note?: string) =>
      request<{ post: Post }>("POST", `/posts/${postId}/needs-revision`, {
        note,
      }),
    /** Awaitable: server runs Gemini and returns the updated post in one response. */
    regenerateCopy: (postId: string, body?: { promptHint?: string }) =>
      request<{ post: Post }>(
        "POST",
        `/posts/${postId}/regenerate-copy`,
        body ?? {},
      ),
  },

  // ─── Generate ─────────────────────────────────────────────────────────────
  generate: {
    dispatch: (data: {
      brandId: string;
      campaignId: string;
      platforms: string[];
      promptHint?: string;
      /** Override the user's profile variations setting for this dispatch (1–3). */
      variationsCount?: number;
    }) => request<{ jobId: string; status: string }>("POST", "/generate", data),
    status: (jobId: string) =>
      request<{
        jobId: string;
        status: string;
        postsCreated: number;
        error: string | null;
      }>("GET", `/generate/status/${jobId}`),
  },

  // ─── Analytics ────────────────────────────────────────────────────────────
  analytics: {
    overview: () => request<AnalyticsOverview>("GET", "/analytics/overview"),
    activity: (days = 30) =>
      request<{ activity: DailyActivity[] }>(
        "GET",
        `/analytics/activity?days=${days}`,
      ),
    campaigns: () =>
      request<{ campaigns: CampaignSummary[] }>("GET", "/analytics/campaigns"),
    campaign: (campaignId: string) =>
      request<CampaignBreakdown>("GET", `/analytics/campaigns/${campaignId}`),
    engagement: () =>
      request<{
        topPosts: TopEngagedPost[];
        byPlatform: EngagementByPlatform[];
      }>("GET", "/analytics/engagement"),
  },

  // ─── Stock images ─────────────────────────────────────────────────────────
  stockImages: {
    search: (source: "pexels" | "pixabay", q: string, page = 1, perPage = 20) =>
      request<StockPhotoResult>(
        "GET",
        `/stock-images/search?source=${source}&q=${encodeURIComponent(q)}&page=${page}&per_page=${perPage}`,
      ),
  },

  // ─── Billing ──────────────────────────────────────────────────────────────
  billing: {
    plans: () =>
      request<{ current: PlanInfo; plans: PlanDef[] }>("GET", "/billing/plans"),
    checkout: (plan: string) =>
      request<{ url: string }>("POST", "/billing/checkout", { plan }),
    portal: () => request<{ url: string }>("POST", "/billing/portal"),
    /** Purchase a token pack (Business plan only — 1,000 tokens for $10). */
    buyTokens: () => request<{ url: string }>("POST", "/billing/tokens"),
    /** Re-read live Stripe state and correct the local DB record. */
    sync: () =>
      request<{ plan: string; status: string; synced: boolean }>(
        "POST",
        "/billing/sync",
      ),
  },

  // ─── Team / organization ──────────────────────────────────────────────────
  team: {
    /** Get org info + member list for the current user's organization. */
    get: () =>
      request<{
        org: { orgId: string; name: string } | null;
        members: OrgMember[];
        isMember: boolean;
        isOwner?: boolean;
      }>("GET", "/team"),
    /** Create a new organization (Business+ plan required). */
    createOrg: (name?: string) =>
      request<{ org: { orgId: string; name: string } }>("POST", "/team", {
        name,
      }),
    /** Invite a member by email. Returns the invite URL. */
    invite: (email: string) =>
      request<{ inviteUrl: string; email: string }>("POST", "/team/invite", {
        email,
      }),
    /** Remove a member from the org (owner only). */
    removeMember: (email: string) =>
      request<{ success: boolean }>(
        "DELETE",
        `/team/members/${encodeURIComponent(email)}`,
      ),
    /** Accept an invite token (must be authenticated). */
    acceptInvite: (token: string) =>
      request<{ success: boolean; orgName: string }>(
        "POST",
        `/team/invite/${token}/accept`,
        {},
      ),
  },
};

// ─── Shared types (mirrors worker/src/types/manifests.ts) ─────────────────────

export interface CampaignSuggestion {
  name?: string;
  objective?: string;
  product?: {
    name?: string;
    description?: string;
    pricePoint?: string;
    differentiators?: string[];
  };
  keyMessages?: string[];
  sourceNotes?: string;
  suggestedImageUrls?: string[];
}

export interface BrandSuggestion {
  name?: string;
  industry?: string;
  targetAudience?: string;
  voice?: string;
  colors?: { primary: string; secondary: string };
  sourceNotes?: string;
}

export interface VoiceSuggestion {
  voice: string;
  reason: string;
}

export interface Brand {
  brandId: string;
  userId: string;
  name: string;
  logoR2Key: string | null;
  voice: string;
  colors: { primary: string; secondary: string };
  targetAudience: string;
  industry: string;
  hashtagSets: HashtagSet[];
  rejectionNotes: string[];
  createdAt: number;
  updatedAt: number;
}

export interface HashtagSet {
  id: string;
  label: string;
  hashtags: string[];
}

export interface Campaign {
  campaignId: string;
  brandId: string;
  userId: string;
  name: string;
  objective: string;
  product: {
    name: string;
    description: string;
    pricePoint?: string;
    differentiators: string[];
  };
  startDate: string;
  endDate: string;
  platforms: string[];
  cadence: { platform: string; postsPerWeek: number }[];
  keyMessages: string[];
  ctas: { id: string; label: string; url: string; utmUrl?: string }[];
  status: string;
  /** R2 keys for campaign images; index 0 is the primary image used for posts. */
  assetR2Keys: string[];
  includeLinkBacks: boolean;
  /** The URL analyzed to pre-fill this campaign, retained for re-analysis. */
  sourceUrl?: string;
  createdAt: number;
  updatedAt: number;
}

export interface Post {
  postId: string;
  campaignId: string;
  brandId: string;
  userId: string;
  platform: string;
  copy: string;
  hashtags: string[];
  imageR2Key: string | null;
  openGraph: OpenGraphMeta | null;
  status: string;
  scheduledAt: number | null;
  publishedAt: number | null;
  rejectionNote: string | null;
  editHistory: EditHistoryEntry[];
  approvedBy: string | null;
  generatedPromptHint: string | null;
  groupId: string | null;
  threadOrder: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface OpenGraphMeta {
  title: string;
  description: string;
  imageR2Key: string | null;
  url: string;
  utmUrl: string;
}

export interface EditHistoryEntry {
  field: string;
  before: string;
  after: string;
  editedAt: number;
}

export interface AnalyticsOverview {
  postsGenerated: number;
  postsApproved: number;
  postsRejected: number;
  postsScheduled: number;
  postsPublished: number;
  approvalRate: number;
  cycleUsage: {
    used: number;
    limit: number;
    plan: string;
    periodEnd: number | null;
  };
  engagement: {
    likes: number;
    comments: number;
    shares: number;
    impressions: number;
    clicks: number;
  };
}

export interface DailyActivity {
  day: string;
  generated: number;
  approved: number;
  rejected: number;
  published: number;
}

export interface CampaignSummary {
  campaignId: string;
  name: string;
  total: number;
  approved: number;
  rejected: number;
  published: number;
  platforms: string[];
  approvalRate: number;
}

export interface PlatformStats {
  total: number;
  approved: number;
  rejected: number;
  published: number;
}

export interface CampaignBreakdown {
  campaignId: string;
  total: number;
  approved: number;
  rejected: number;
  published: number;
  approvalRate: number;
  byPlatform: Record<string, PlatformStats>;
  engagement: {
    likes: number;
    comments: number;
    shares: number;
    impressions: number;
    clicks: number;
  };
}

export interface EngagementByPlatform {
  platform: string;
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
}

export interface TopEngagedPost {
  postId: string;
  platform: string;
  campaignId: string;
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
  clicks: number;
  updatedAt: number;
}

export interface PlanInfo {
  plan: string;
  status: string;
  used: number;
  /** Generation runs allowed this cycle. -1 = unlimited. */
  limit: number;
  periodEnd: number | null;
  /** "month" or "year" */
  interval: string;
}

export interface PlanCapabilities {
  publish: boolean;
  analytics: boolean;
  team: boolean;
}

export interface PlanDef {
  id: string;
  name: string;
  tagline: string;
  /** USD/mo billed monthly. */
  priceMonthly: number;
  /** USD/mo billed annually (the anchored rate). */
  priceAnnual: number;
  /** AI generation runs per month. -1 = unlimited. */
  runsPerMonth: number;
  /** Number of brands allowed. -1 = unlimited. */
  brands: number;
  /** Total org seats incl. owner. */
  teamSeats: number;
  capabilities: PlanCapabilities;
  highlighted: boolean;
}

export interface UserProfile {
  userId: string;
  email: string;
  authProvider: "clerk";
  plan: string;
  onboardingCompleted: boolean;
  socialConnectOnboardingCompleted: boolean;
  socialConnectSkippedAt?: number;
  timezone: string;
  emailNotifications: boolean;
  /** How many distinct AI variations to generate per platform per batch (1–3). Default 1. */
  postVariationsPerPlatform?: number;
  createdAt: number;
  updatedAt: number;
}

export type SocialProvider =
  "meta_instagram" | "meta_facebook" | "linkedin" | "x" | "bluesky";

export interface SocialConnection {
  provider: SocialProvider;
  externalId: string;
  displayName: string | null;
  accountType: string | null;
  connectedAt: number;
  updatedAt: number;
}

export interface PostEngagement {
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
  clicks: number;
}

// ─── Stock images ─────────────────────────────────────────────────────────────

export interface StockPhoto {
  id: string;
  source: "pexels" | "pixabay";
  /** Small thumbnail — used in the picker grid (~130–150 px wide). */
  thumbUrl: string;
  /** Medium preview — used for hover/lightbox (~640 px wide). */
  previewUrl: string;
  /** Full URL passed to assets/from-url when the user selects the image. */
  downloadUrl: string;
  /** Photo page on Pexels/Pixabay — required for attribution. */
  pageUrl: string;
  photographer: string;
  photographerUrl: string;
  alt: string;
}

export interface StockPhotoResult {
  photos: StockPhoto[];
  total: number;
  page: number;
  perPage: number;
  source: "pexels" | "pixabay";
}

// ─── Team types ───────────────────────────────────────────────────────────────

export interface OrgMember {
  email: string;
  userId: string | null;
  role: string;
  status: "pending" | "active" | "removed";
  inviteToken: string | null;
  invitedAt: number;
  joinedAt: number | null;
}

export interface InviteMetadata {
  orgName: string;
  email: string;
  valid: boolean;
}

/** Public (no-auth) fetch for invite metadata — used on the /invite/:token page. */
export async function fetchInviteMetadata(
  token: string,
): Promise<InviteMetadata> {
  const res = await fetch(`/public/team/invite/${token}`);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
      error?: string;
    };
    throw new ApiError(err.error ?? res.statusText, res.status);
  }
  return res.json() as Promise<InviteMetadata>;
}
